/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppFab from './components/WhatsAppFab';
import { Toaster, toast } from 'react-hot-toast';
import { monitorConnection } from './lib/firebase';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import Wholesale from './pages/Wholesale';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Admin from './pages/Admin';
import Wishlist from './pages/Wishlist';
import Cart from './pages/Cart';
import Orders from './pages/Orders';
import ProductDetail from './pages/ProductDetail';
import FacilityLogin from './pages/FacilityLogin';
import FacilityDashboard from './pages/FacilityDashboard';
import Login from './pages/Login';
import Brochure from './pages/Brochure';
import Account from './pages/Account';
import MobileBottomNav from './components/MobileBottomNav';

export default function App() {
  const [location] = useLocation();
  const isStoreFeedPage = location === '/' || location === '/products';
  const isFacilityPage = location === '/facility-login' || location === '/unit-dashboard';
  const isAdminPage = location.startsWith('/admin');

  React.useEffect(() => {
    const unmonitor = monitorConnection(() => {
      toast.error("Connecting to server taking longer than usual. Please check your internet or disable ad-blockers.", {
        id: 'connection-warning',
        duration: 8000
      });
    });
    return unmonitor;
  }, []);

  return (
    <AuthProvider>
      <WishlistProvider>
        <CartProvider>
          <div className="flex flex-col min-h-screen font-sans bg-spice-light text-spice-text">
          {/* Responsive Header: On store feed pages StoreHeader is rendered inside Home/Products; on all other pages show Navbar */}
          {!isFacilityPage && !isAdminPage && !isStoreFeedPage && (
            <Navbar />
          )}
          <main className="flex-grow">
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/about" component={About} />
              <Route path="/products" component={Products} />
              <Route path="/wholesale" component={Wholesale} />
              <Route path="/contact" component={Contact} />
              <Route path="/blog" component={Blog} />
              <Route path="/admin" component={Admin} />
              <Route path="/wishlist" component={Wishlist} />
              <Route path="/cart" component={Cart} />
              <Route path="/orders" component={Orders} />
              <Route path="/account" component={Account} />
              <Route path="/login" component={Login} />
              <Route path="/brochure" component={Brochure} />
              <Route path="/product/:id" component={ProductDetail} />
              <Route path="/facility-login" component={FacilityLogin} />
              <Route path="/unit-dashboard" component={FacilityDashboard} />
              
              {/* 404 Route */}
            <Route>
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-6">Page not found</p>
                  <a href="/" className="text-amber-600 font-medium hover:underline">
                    Return Home
                  </a>
                </div>
              </div>
            </Route>
          </Switch>
        </main>
        
        {/* Footer: Hidden on mobile layouts with dedicated feeds, visible on desktop and content pages */}
        {!isFacilityPage && !isAdminPage && (
          <div className={isStoreFeedPage ? "hidden lg:block" : "block"}>
            <Footer />
          </div>
        )}

        {/* Global Mobile Bottom Navigation Bar across customer views */}
        <MobileBottomNav />

        {/* WhatsApp FAB on customer pages */}
        {!isFacilityPage && !isAdminPage && location !== '/facility-login' && <WhatsAppFab />}
        <Toaster 
          position="bottom-right" 
          toastOptions={{
            style: {
              borderRadius: '0',
              background: '#1e293b',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              border: '1px solid rgba(255,255,255,0.1)'
            }
          }}
        />
      </div>
    </CartProvider>
    </WishlistProvider>
    </AuthProvider>
  );
}
